import { authFetch } from '../../../api/csrf';
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast, Toaster } from "react-hot-toast";
import {
  Plus,
  Search,
  Calendar,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  Edit,
  Trash2,
  Play,
  Target,
  X,
  Eye,
  FileText,
  Download,
  Upload,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  ChevronDown
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import { API_ENDPOINTS } from "../../../api/config";
import SearchableDropdown from "../../../components/common/SearchableDropdown";
import PermissionGate from "../../../components/common/PermissionGate";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import BulkUploadModal from "../../../components/common/BulkUploadModal";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { formatTimeTo12Hour, formatIndianNumber } from "../../../utils/amountFormatter";
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
} from "../../../utils/inputValidation";
import opportunityService from "../../../api/crm/opportunities/opportunityService";
import leadService from "../../../api/crm/leads/leadService";
import fieldSalesService from "../../../api/crm/field-sales/fieldSalesService";
import taskService from "../../../api/crm/tasks/taskService";
import { formatDate } from "../../../utils/stringUtils";
import { useAuth } from "../../../context/AuthContext";
import { USER_ROLES } from "../../../utils/constants";

const capitalizeFirst = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

function sortTasksList(items, sortColumn, sortDirection) {
  const tasksToSort = items;
  if (!sortColumn || !tasksToSort || tasksToSort.length === 0) {
    return tasksToSort;
  }

  const sorted = [...tasksToSort].sort((a, b) => {
    let aValue;
    let bValue;

    switch (sortColumn) {
      case "dueDate":
      case "dueDateRaw":
        aValue = a.dueDateRaw ? new Date(a.dueDateRaw).getTime() : 0;
        bValue = b.dueDateRaw ? new Date(b.dueDateRaw).getTime() : 0;
        break;
      case "createdDate":
      case "createdDateRaw":
        aValue = a.createdDateRaw ? new Date(a.createdDateRaw).getTime() : 0;
        bValue = b.createdDateRaw ? new Date(b.createdDateRaw).getTime() : 0;
        break;
      case "lastModified":
      case "lastModifiedRaw":
        aValue = a.lastModifiedRaw ? new Date(a.lastModifiedRaw).getTime() : 0;
        bValue = b.lastModifiedRaw ? new Date(b.lastModifiedRaw).getTime() : 0;
        break;
      case "title":
      case "type":
      case "owner":
      case "priority":
      case "status":
        aValue = (a[sortColumn] || "").toLowerCase();
        bValue = (b[sortColumn] || "").toLowerCase();
        break;
      default:
        aValue = a[sortColumn] ?? "";
        bValue = b[sortColumn] ?? "";
        if (typeof aValue === "string") aValue = aValue.toLowerCase();
        if (typeof bValue === "string") bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
}

const TasksFollowups = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const isSalesManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialOwner = searchParams.get('owner') || 'all';
  const initialPriority = searchParams.get('priority') || 'all';
  const initialStatus = searchParams.get('status') || 'all';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const initialSortBy = searchParams.get('sortBy') || null;
  const initialSortDir = searchParams.get('sortOrder') || 'desc';

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filterOwner, setFilterOwner] = useState(initialOwner);
  const [filterPriority, setFilterPriority] = useState(initialPriority);
  const [filterStatus, setFilterStatus] = useState(initialStatus);
  const [selectedTask, setSelectedTask] = useState(null);
  // const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [formErrors, setFormErrors] = useState({});
  const searchInputRef = useRef(null);
  const [opportunitySearchTerm, setOpportunitySearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [pagination, setPagination] = useState({});
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Assignee dropdown state (filter)
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [filteredAssigneeOptions, setFilteredAssigneeOptions] = useState([]);
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  // Assignee dropdown state (modal)
  const [modalAssigneeOptions, setModalAssigneeOptions] = useState([]);
  const [loadingModalAssignees, setLoadingModalAssignees] = useState(false);
  const [modalAssigneeSearchTerm, setModalAssigneeSearchTerm] = useState('');
  const [filteredModalAssigneeOptions, setFilteredModalAssigneeOptions] = useState([]);
  const [openModalAssigneeDropdown, setOpenModalAssigneeDropdown] = useState(false);
  const [modalAssigneeDropdownPosition, setModalAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const modalAssigneeDropdownRef = useRef(null);

  const currentUserId = user?._id || user?.id;

  // Sorting state
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDir);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'owner', 'priority', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (filterOwner && filterOwner !== 'all') params.set('owner', filterOwner);
      if (filterPriority && filterPriority !== 'all') params.set('priority', filterPriority);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn) {
        params.set('sortBy', sortColumn);
        if (sortDirection && sortDirection !== 'asc') params.set('sortOrder', sortDirection);
      }

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterOwner,
    filterPriority,
    filterStatus,
    dateRange,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams,
  ]);

  // Get today's date for date validation
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;

  // Calculate minimum allowed date for editing
  const getMinDate = useMemo(() => {
    return () => {
      if (isEditing && selectedTask && selectedTask.dueDateRaw) {
        // Extract date string directly from ISO format to avoid timezone issues
        let originalDateStr = '';
        if (typeof selectedTask.dueDateRaw === 'string') {
          // If it's already in YYYY-MM-DD format, use it directly
          if (selectedTask.dueDateRaw.match(/^\d{4}-\d{2}-\d{2}$/)) {
            originalDateStr = selectedTask.dueDateRaw;
          } else {
            // Extract YYYY-MM-DD from ISO string (before the 'T')
            originalDateStr = selectedTask.dueDateRaw.split('T')[0];
          }
        } else {
          // If it's a Date object, format it as YYYY-MM-DD in local time
          const date = new Date(selectedTask.dueDateRaw);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          originalDateStr = `${year}-${month}-${day}`;
        }

        // Compare dates as strings (YYYY-MM-DD format)
        // If original date was in the past, allow editing up to that date
        if (originalDateStr < todayStr) {
          return originalDateStr;
        }
      }
      // For create mode or if original date is today/future, use today
      return todayStr;
    };
  }, [isEditing, selectedTask, todayStr]);

  const minDate = getMinDate();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // React Query: Fetch tasks with filters and pagination
  const {
    data: tasksData,
    isLoading: loadingTasks,
    isError: tasksError,
  } = useQuery({
    queryKey: [
      "tasks",
      filterOwner,
      filterPriority,
      filterStatus,
      debouncedSearchTerm,
      dateRange,
      currentPage,
      itemsPerPage,
      user?.role,
      user?._id || user?.id,
    ],
    queryFn: async () => {
      const filters = {
        status:
          filterStatus !== "all"
            ? (filterStatus === "overdue" || filterStatus === "Overdue" ? "overdue" : capitalizeFirst(filterStatus))
            : undefined,
        ownerId: filterOwner !== "all" ? filterOwner : undefined,
        priority:
          filterPriority !== "all"
            ? capitalizeFirst(filterPriority)
            : undefined,
        search: debouncedSearchTerm || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };
      const response = await taskService.getTasks(filters);
      if (response.success && response.tasks) {
        // Store pagination data
        if (response.pagination) {
          setPagination(response.pagination);
        }
        return response.tasks;
      } else {
        throw new Error(response.error || "Failed to load tasks");
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to load tasks");
    },
  });

  // React Query: Fetch task statistics
  const { data: taskStatsData, isLoading: loadingStats } = useQuery({
    queryKey: ["taskStats", user?.role, user?._id || user?.id],
    queryFn: async () => {
      const response = await taskService.getTaskStats(); // No filters
      if (response.success && response.stats) {
        return response.stats;
      } else {
        throw new Error(response.error || "Failed to load task statistics");
      }
    },
  });

  // Map backend task to frontend format
  const mapTaskToFrontend = (task) => {
    const opp = task.opportunityId || task.leadId;
    let oppData = null;
    if (opp) {
      if (typeof opp === 'string') {
        oppData = { id: opp, name: opp, account: "" };
      } else {
        const leadName = [opp.firstName, opp.lastName].filter(Boolean).join(' ').trim();
        const displayName = opp.opportunityName || opp.name || leadName || opp.company || opp.leadId || "Unnamed Opportunity";
        oppData = {
          id: opp._id || opp.id,
          name: displayName,
          leadId: opp.leadId || "",
          account: opp.accountName || opp.company || ""
        };
      }
    }

    return {
      id: task._id || task.id,
      taskId: task.taskId || "",
      title: task.title,
      type: task.type,
      description: task.description,
      dueDate: task.dueDate || "",
      dueDateRaw: task.dueDate, // Keep raw date for date input
      dueTime: task.dueTime,
      owner: task.owner,
      ownerId: task.ownerId?._id || task.ownerId || "",
      ownerEmail: task.ownerEmail || "",
      priority: task.priority,
      status: task.status,
      opportunity: oppData,
      createdDate: task.createdAt || "",
      createdDateRaw: task.createdAt,
      lastModified: task.lastModified || task.updatedAt || "",
      lastModifiedRaw: task.lastModified || task.updatedAt,
      completedDate: task.completedDate || null,
      completedDateRaw: task.completedDate,
      notes: task.notes,
      createdByName: task.createdByName || "",
      updatedByName: task.updatedByName || "",
      techRequired: task.techRequired || "",
    };
  };

  const tasks = useMemo(() => {
    if (!tasksData) return [];
    return tasksData.map(mapTaskToFrontend);
  }, [tasksData]);

  const stats = useMemo(() => {
    if (!taskStatsData) {
      return {
        total: 0,
        completed: 0,
        pending: 0,
        inProgress: 0,
        overdue: 0,
        completionRate: 0,
      };
    }
    return {
      total: taskStatsData.total || 0,
      completed: taskStatsData.completed || 0,
      pending: taskStatsData.pending || 0,
      inProgress: 0,
      overdue: taskStatsData.overdue || 0,
      completionRate: parseFloat(taskStatsData.completionRate || 0),
    };
  }, [taskStatsData]);

  // React Query Mutations
  const createTaskMutation = useMutation({
    mutationFn: async (taskData) => {
      const response = await taskService.createTask(taskData);
      if (response.success) {
        return response.task;
      } else {
        throw new Error(response.error || "Failed to create task");
      }
    },
    onSuccess: () => {
      toast.success("Task created successfully!");
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskStats"] });
      setShowCreateModal(false);
      setIsEditing(false);
      setSelectedTask(null);
      setOriginalTaskValues(null);
      setCreateForm({
        title: "",
        type: "",
        description: "",
        dueDate: "",
        dueTime: "",
        owner: "",
        priority: "Low",
        status: "Pending",
        notes: "",
        opportunity: "",
        techRequired: "",
      });
      setOpportunitySearchTerm("");
      setModalAssigneeSearchTerm("");
      setFormErrors({});
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create task");
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, taskData }) => {
      const response = await taskService.updateTask(taskId, taskData);
      if (response.success) {
        return response.task;
      } else {
        throw new Error(response.error || "Failed to update task");
      }
    },
    onSuccess: () => {
      toast.success("Task updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskStats"] });
      setShowCreateModal(false);
      setIsEditing(false);
      setSelectedTask(null);
      setOriginalTaskValues(null);
      setCreateForm({
        title: "",
        type: "",
        description: "",
        dueDate: "",
        dueTime: "",
        owner: "",
        priority: "Low",
        status: "Pending",
        notes: "",
        opportunity: "",
        techRequired: "",
      });
      setOpportunitySearchTerm("");
      setModalAssigneeSearchTerm("");
      setFormErrors({});
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update task");
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId) => {
      const response = await taskService.deleteTask(taskId);
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || "Failed to delete task");
      }
    },
    onSuccess: () => {
      toast.success("Task deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskStats"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete task");
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: async (taskId) => {
      const response = await taskService.completeTask(taskId);
      if (response.success) {
        return response.task;
      } else {
        throw new Error(response.error || "Failed to complete task");
      }
    },
    onSuccess: () => {
      toast.success("Task marked as completed!");
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskStats"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to complete task");
    },
  });

  const [createForm, setCreateForm] = useState({
    title: "",
    type: "",
    description: "",
    dueDate: "",
    dueTime: "",
    owner: "",
    priority: "Low",
    status: "Pending",
    notes: "",
    opportunity: "",
    techRequired: "",
  });

  // Store original task values for change detection
  const [originalTaskValues, setOriginalTaskValues] = useState(null);

  const taskTypes = ["Call", "Meeting", "Follow-up", "Demo", "Email", "Custom"];
  const priorities = ["Low", "Medium", "High"];
  const statuses = ["Pending", "Completed", "Cancelled", "Overdue"];
  // Load CRM users for assignee selection (matching LeadListFilters pattern)
  const loadAssignees = React.useCallback(async (target = 'filter') => {
    const setLoading = target === 'modal' ? setLoadingModalAssignees : setLoadingAssignees;
    const setOptions = target === 'modal' ? setModalAssigneeOptions : setAssigneeOptions;
    const setFiltered = target === 'modal' ? setFilteredModalAssigneeOptions : setFilteredAssigneeOptions;

    if (isSalesExecutive && target === 'filter') {
      setOptions([]);
      setFiltered([]);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', 'all');

      if (user?.role === USER_ROLES.SUPER_ADMIN) {
        params.append('roleFilter', 'sales');
      } else if (isSalesManager) {
        params.append('roleFilter', user?.role === USER_ROLES.EXPORT_MANAGER ? 'export_executive' : 'sales_executive');
        params.append('managerId', user._id || user.id);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      let exportManagers = [];
      if (user?.role === USER_ROLES.SALES_MANAGER) {
        try {
          const emResponse = await authFetch(`${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?limit=all&roleFilter=sales`, {
            credentials: 'include',
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          const emResult = await emResponse.json();
          if (emResult.responseCode === 200 && emResult.data?.users) {
            exportManagers = emResult.data.users.filter(u => u.role === USER_ROLES.EXPORT_MANAGER || u.designation === 'Export Manager');
          }
        } catch (e) {
        }
      }

      if (result.responseCode === 200 && result.data?.users) {
        const crmUsers = result.data.users.map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || 'Unknown',
          email: u.email || '',
          role: u.role || u.designation || ''
        }));
        const mappedExportManagers = exportManagers.map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || 'Unknown',
          email: u.email || '',
          role: u.role || u.designation || ''
        }));

        const allUsers = [...crmUsers, ...mappedExportManagers];
        const uniqueUsersMap = new Map();
        allUsers.forEach(u => {
          if (u.id) uniqueUsersMap.set(u.id, u);
        });
        const mergedUsers = Array.from(uniqueUsersMap.values());

        // If the logged-in user is a manager, add them to the list
        const managerOption = (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER)
          ? [{
            id: currentUserId,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
            email: user?.email || '',
            role: user?.role || ''
          }]
          : [];

        if (target === 'filter') {
          const baseOptions = [
            { id: 'all', name: 'All Representatives', email: '', role: '' },
          ];
          setOptions([...baseOptions, ...managerOption, ...mergedUsers]);
          setFiltered([...baseOptions, ...managerOption, ...mergedUsers]);
        } else {
          // For modal, include executives themselves too
          const execOption = isSalesExecutive
            ? [{
              id: currentUserId,
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
              email: user?.email || '',
              role: user?.role || ''
            }]
            : [];
          const combined = [...managerOption, ...execOption, ...mergedUsers];
          // Deduplicate
          const dedupMap = new Map();
          combined.forEach(u => { if (u.id) dedupMap.set(u.id, u); });
          const finalList = Array.from(dedupMap.values());
          setOptions(finalList);
          setFiltered(finalList);
        }
      }
    } catch (error) {
      if (target === 'filter') {
        const defaultOptions = [{ id: 'all', name: 'All Representatives', email: '', role: '' }];
        setOptions(defaultOptions);
        setFiltered(defaultOptions);
      } else {
        setOptions([]);
        setFiltered([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user, isSalesManager, isSalesExecutive, currentUserId]);

  // Load filter assignees on mount
  useEffect(() => {
    loadAssignees('filter');
  }, [loadAssignees]);

  // Load modal assignees when modal opens
  useEffect(() => {
    if (showCreateModal) {
      loadAssignees('modal');
    }
  }, [showCreateModal, loadAssignees]);

  // Filter assignee options based on search (filter dropdown)
  useEffect(() => {
    if (!openAssigneeDropdown) return;
    const handler = setTimeout(() => {
      if (!assigneeSearchTerm.trim()) {
        setFilteredAssigneeOptions(assigneeOptions);
      } else {
        const term = assigneeSearchTerm.toLowerCase();
        const filtered = assigneeOptions.filter(option =>
          option.name.toLowerCase().includes(term) ||
          option.email.toLowerCase().includes(term)
        );
        setFilteredAssigneeOptions(filtered);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeSearchTerm, openAssigneeDropdown, assigneeOptions]);

  // Filter assignee options based on search (modal dropdown)
  useEffect(() => {
    if (!openModalAssigneeDropdown) return;
    const handler = setTimeout(() => {
      if (!modalAssigneeSearchTerm.trim()) {
        setFilteredModalAssigneeOptions(modalAssigneeOptions);
      } else {
        const term = modalAssigneeSearchTerm.toLowerCase();
        const filtered = modalAssigneeOptions.filter(option =>
          option.name.toLowerCase().includes(term) ||
          option.email.toLowerCase().includes(term)
        );
        setFilteredModalAssigneeOptions(filtered);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [modalAssigneeSearchTerm, openModalAssigneeDropdown, modalAssigneeOptions]);

  // Calculate assignee dropdown position (filter)
  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  // Calculate assignee dropdown position (modal)
  useEffect(() => {
    if (openModalAssigneeDropdown && modalAssigneeDropdownRef.current) {
      const rect = modalAssigneeDropdownRef.current.getBoundingClientRect();
      setModalAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openModalAssigneeDropdown]);

  // Handle click outside assignee dropdown (filter)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-task-assignee-filter]');
        if ((dropdownElement && !dropdownElement.contains(event.target)) || !dropdownElement) {
          setOpenAssigneeDropdown(false);
          setAssigneeSearchTerm('');
        }
      }
    };
    if (openAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openAssigneeDropdown]);

  // Handle click outside assignee dropdown (modal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalAssigneeDropdownRef.current && !modalAssigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-task-assignee-modal]');
        if ((dropdownElement && !dropdownElement.contains(event.target)) || !dropdownElement) {
          setOpenModalAssigneeDropdown(false);
          setModalAssigneeSearchTerm('');
        }
      }
    };
    if (openModalAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openModalAssigneeDropdown]);

  // React Query: Fetch converted opportunities with search (for create/edit modal)
  const {
    data: opportunitiesData,
    isLoading: loadingOpportunities,
    isError: opportunitiesError,
  } = useQuery({
    queryKey: ["opportunities", "converted", opportunitySearchTerm, user?.role, user?._id || user?.id],
    queryFn: async () => {
      // We need both 'converted' and 'closed_lost' stages.
      // Fetching all converted leads might be enough if the backend returns all of them.
      // Based on leadService.getConvertedLeads implementation, we can pass a stage.

      const fetchByStage = async (stage) => {
        const params = {
          limit: 100,
          stage,
          ...(opportunitySearchTerm && opportunitySearchTerm.length >= 2
            ? { search: opportunitySearchTerm }
            : {}),
        };
        const res = await leadService.getConvertedLeads(params);
        return res.success ? res.leads : [];
      };

      const [convertedLeads, lostLeads] = await Promise.all([
        fetchByStage('converted'),
        fetchByStage('closed_lost')
      ]);

      // Merge and remove duplicates if any
      const merged = [...convertedLeads, ...lostLeads];
      const unique = Array.from(new Map(merged.map(item => [item._id || item.id, item])).values());

      return unique;
    },
    enabled: showCreateModal, // Only fetch when modal is open
    onError: (error) => {
      toast.error(error.message || "Failed to load opportunities");
    },
  });

  const opportunities = opportunitiesData || [];

  // Convert opportunities to SearchableDropdown format
  const opportunityOptions = useMemo(() => {
    return opportunities.map((opportunity) => {
      const companyName = opportunity.company || opportunity.accountName || "";
      const oppName = opportunity.opportunityName || opportunity.name || "Unnamed Opportunity";
      const customerName = opportunity.customerName || "";

      return {
        id: opportunity._id || opportunity.id,
        displayText: `${oppName}${companyName ? ` (${companyName})` : ""} - ${opportunity.stage === 'closed_lost' ? 'Lost' : 'Open'}`,
        searchText: `${oppName} ${companyName} ${customerName} ${opportunity.stage || ""}`.toLowerCase(),
        title: oppName,
        raw: opportunity,
        opportunity: {
          id: opportunity._id || opportunity.id,
          name: oppName,
          account: companyName,
        }
      };
    });
  }, [opportunities]);

  const sortedTasks = useMemo(
    () => sortTasksList(tasks, sortColumn, sortDirection),
    [tasks, sortColumn, sortDirection]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== "" ||
      filterOwner !== "all" ||
      filterPriority !== "all" ||
      filterStatus !== "all" ||
      dateRange.startDate !== "" ||
      dateRange.endDate !== ""
    );
  }, [searchTerm, filterOwner, filterPriority, filterStatus, dateRange]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Calculate pagination display values
  const effectivePaginationLimit = pagination?.limit || itemsPerPage;
  const totalTaskPages = Number(
    pagination?.pages || pagination?.totalPages || 0
  );
  const totalTaskCount = Number(pagination?.total || 0);
  const shouldShowPagination =
    !loadingTasks &&
    totalTaskPages > 1 &&
    totalTaskCount > effectivePaginationLimit;

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterOwner("all");
    setFilterPriority("all");
    setFilterStatus("all");
    setDateRange({ startDate: "", endDate: "" });
    setCurrentPage(1);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "bg-red-100 text-red-800";
      case "Medium":
        return "bg-yellow-100 text-yellow-800";
      case "Low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Cancelled":
        return "bg-red-100 text-red-800";
      case "Overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case "Call":
        return "📞";
      case "Meeting":
        return "🤝";
      case "Follow-up":
        return "🔄";
      case "Demo":
        return "🎯";
      case "Email":
        return "📧";
      case "Custom":
        return "📝";
      default:
        return "📋";
    }
  };

  const isOverdue = (dueDateRaw, dueTime) => {
    if (!dueDateRaw || !dueTime) return false;
    try {
      const now = new Date();
      // Handle both Date objects and ISO strings
      const dueDate =
        dueDateRaw instanceof Date ? dueDateRaw : new Date(dueDateRaw);
      const year = dueDate.getFullYear();
      const month = String(dueDate.getMonth() + 1).padStart(2, '0');
      const day = String(dueDate.getDate()).padStart(2, '0');
      const dueDateStr = `${year}-${month}-${day}`;
      const due = new Date(`${dueDateStr}T${dueTime}`);
      return now > due;
    } catch (error) {
      return false;
    }
  };

  const handleBlur = (field) => {
    const value = createForm[field];
    let errorMessage = "";

    if (!value || (typeof value === "string" && !value.trim())) {
      switch (field) {
        case "title":
          errorMessage = "Task Title is required";
          break;
        case "type":
          errorMessage = "Task Type is required";
          break;
        case "description":
          errorMessage = "Description is required";
          break;
        case "opportunity":
          errorMessage = "Opportunity is required";
          break;
        case "dueDate":
          errorMessage = "Due Date is required";
          break;
        case "dueTime":
          errorMessage = "Due Time is required";
          break;
        case "owner":
          errorMessage = "Representative is required";
          break;
        case "techRequired":
          errorMessage = "Tech Required field is required";
          break;
        default:
          errorMessage = `${field} is required`;
      }
    }

    if (errorMessage) {
      setFormErrors((prev) => ({
        ...prev,
        [`create_${field}`]: errorMessage,
      }));
    }
  };

  const handleCreateTask = () => {
    setIsEditing(false);
    setSelectedTask(null);
    setOriginalTaskValues(null);
    setOpportunitySearchTerm(""); // Reset opportunity search
    setModalAssigneeSearchTerm(""); // Reset assignee search
    setCreateForm({
      title: "",
      type: "",
      description: "",
      dueDate: "",
      dueTime: "",
      owner: isSalesExecutive ? currentUserId : "",
      priority: "Low",
      status: "Pending",
      notes: "",
      opportunity: "",
      techRequired: "",
    });
    setShowCreateModal(true);
  };

  const handleEditTask = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    setSelectedTask(task);
    setIsEditing(true);

    // Store original values for change detection
    setOriginalTaskValues({
      type: task.type,
      dueDate: task.dueDateRaw,
      dueTime: task.dueTime,
    });

    // Format dueDate for date input (YYYY-MM-DD) using local time to avoid timezone issues
    const formatDateForInput = (dateValue) => {
      if (!dateValue) return "";
      if (typeof dateValue === "string") {
        // If it's already in YYYY-MM-DD format, use it directly
        if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateValue;
        }
        // Otherwise parse and format
      }
      // Format as YYYY-MM-DD in local time
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const dueDateForInput = formatDateForInput(task.dueDateRaw);
    setCreateForm({
      title: task.title,
      type: task.type,
      description: task.description,
      dueDate: dueDateForInput,
      dueTime: task.dueTime,
      owner: task.ownerId || "",
      ownerDisplayName: task.owner?.trim() || "",
      ownerEmail: task.ownerEmail || "",
      priority: task.priority,
      status: task.status || "Pending",
      notes: task.notes || "",
      opportunity: task.opportunity ? task.opportunity.id : "",
      techRequired: task.techRequired || "",
    });
    setModalAssigneeSearchTerm(""); // Reset assignee search
    setOpportunitySearchTerm(task.opportunity?.name || ""); // Set opportunity search to current name to ensure prefill
    setShowCreateModal(true);
  };

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const openCompleteModal = (taskId) => {
    setTaskToComplete(taskId);
    setShowCompleteModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteModal(true);
  };

  const confirmCompleteTask = () => {
    if (taskToComplete) {
      handleCompleteTask(taskToComplete);
      setShowCompleteModal(false);
      setTaskToComplete(null);
    }
  };

  // Confirm delete task
  const confirmDeleteTask = () => {
    if (taskToDelete) {
      deleteTaskMutation.mutate(taskToDelete);
      setShowDeleteModal(false);
      setTaskToDelete(null);
    }
  };

  const cancelCompleteTask = () => {
    setShowCompleteModal(false);
    setTaskToComplete(null);
  };

  // Cancel delete task
  const cancelDeleteTask = () => {
    setShowDeleteModal(false);
    setTaskToDelete(null);
  };

  // const handleViewOpportunity = (opportunityId) => {
  //   navigate(`/opportunity_detail/${opportunityId}`);
  // };

  const handleCompleteTask = (taskId) => {
    completeTaskMutation.mutate(taskId, {
      onError: (error) => {
        toast.error(error.message || "Failed to complete task");
      },
    });
  };

  const [showViewModal, setShowViewModal] = useState(false);
  const [taskToView, setTaskToView] = useState(null);

  const openViewModal = (task) => {
    setTaskToView(task);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setTaskToView(null);
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item.id));
      } else {
        items.forEach(item => newSelection.add(item.id));
      }
      return newSelection;
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) {
      toast.error('Please select tasks to delete');
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const idsToDelete = Array.from(selectedRows);
      const result = await taskService.bulkDeleteTasks(idsToDelete);

      if (result.success) {
        toast.success(result.message || 'Tasks deleted successfully');
        setSelectedRows(new Set());
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
        queryClient.invalidateQueries({ queryKey: ["taskStats"] });
        setShowBulkDeleteConfirm(false);
      } else {
        toast.error(result.error || 'Failed to delete tasks');
      }
    } catch (error) {
      toast.error('An error occurred during bulk delete');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const baseExportFilters = useMemo(
    () => ({
      search: debouncedSearchTerm.trim() || undefined,
      status:
        filterStatus !== "all"
          ? filterStatus === "overdue" || filterStatus === "Overdue"
            ? "overdue"
            : capitalizeFirst(filterStatus)
          : undefined,
      ownerId: filterOwner !== "all" ? filterOwner : undefined,
      priority:
        filterPriority !== "all"
          ? capitalizeFirst(filterPriority)
          : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    }),
    [
      debouncedSearchTerm,
      filterStatus,
      filterOwner,
      filterPriority,
      dateRange.startDate,
      dateRange.endDate,
    ]
  );

  const exportSortBy = useMemo(() => {
    const m = {
      dueDate: "dueDate",
      dueDateRaw: "dueDate",
      createdDate: "createdAt",
      createdDateRaw: "createdAt",
      lastModified: "lastModified",
      lastModifiedRaw: "lastModified",
      title: "title",
      type: "type",
      owner: "owner",
      priority: "priority",
      status: "status",
    };
    return m[sortColumn] || "createdAt";
  }, [sortColumn]);

  const currentPageIdsForExport = useMemo(
    () => sortedTasks.map((t) => String(t.id)).filter(Boolean),
    [sortedTasks]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === "currentPage"
          ? overrideCurrentPageIds ?? currentPageIdsForExport
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          module: "crm-tasks-followups",
          format,
          scope,
          filters: {
            ...baseExportFilters,
            ...(overrideFilters != null ? overrideFilters : {}),
          },
          page: overridePage ?? currentPage,
          limit: overrideLimit ?? itemsPerPage,
          sortBy: exportSortBy,
          sortOrder: sortColumn ? sortDirection : 'desc',
          selectedIds,
          currentPageIds: Array.isArray(pageIds) ? pageIds : [],
        }),
      }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || "Export request failed";
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `tasks-followups-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      exportSortBy,
      sortDirection,
      currentPageIdsForExport,
    ]
  );

  const handleTasksFollowupsSelectedFormatExport = useCallback(
    async (format) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error("No selected rows to export.");
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: "selected",
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} task(s) exported.`);
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || "Export failed.");
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleTasksFollowupsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!["csv", "xlsx", "pdf"].includes(format)) {
        return;
      }

      const pageItems = sortedTasks;
      const totalFiltered = Number(pagination?.total ?? 0);

      setExportLoading(true);
      try {
        if (scope === "currentPage" && !pageItems.length) {
          toast.error("No data on current page to export.");
          return;
        }

        if (scope === "filtered" && totalFiltered === 0) {
          toast.error("No tasks match the current filters.");
          return;
        }

        if (scope === "dateRange") {
          const rangeStart = meta?.startDate;
          const rangeEnd = meta?.endDate;
          if (!rangeStart || !rangeEnd) {
            toast.error("Please select start and end dates.");
            return;
          }
          await requestBackendExport({
            scope: "dateRange",
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate: rangeStart,
              endDate: rangeEnd,
            },
          });
        } else if (scope === "all") {
          await requestBackendExport({
            scope: "all",
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === "filtered") {
          await requestBackendExport({
            scope: "filtered",
            format,
          });
        } else if (scope === "currentPage") {
          await requestBackendExport({
            scope: "currentPage",
            format,
          });
        } else {
          return;
        }

        toast.success("Export downloaded.");
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || "Export failed. Please try again.");
      } finally {
        setExportLoading(false);
      }
    },
    [baseExportFilters, pagination?.total, sortedTasks, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showInlineSelectedFormats]);

  // Helper function to allow only letters, numbers, spaces, and ,.- in description and notes
  const sanitizeText = (value) => {
    return value.replace(/[^a-zA-Z0-9 ,.-]/g, "");
  };

  // Helper function to convert 24-hour time format to 12-hour AM/PM format
  const formatTimeTo12Hour = (time) => {
    if (!time) return "N/A";

    try {
      const [hours, minutes] = time.split(":");
      let hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);

      if (isNaN(hour) || isNaN(minute)) return time;

      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12;
      hour = hour ? hour : 12; // Convert 0 to 12

      return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    } catch (error) {
      return time;
    }
  };
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Tasks & Follow-ups
          </h1>
          <p className="text-gray-600">
            Manage your sales tasks and follow-up activities
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="opportunity_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                onClick={handleExportClick}
                icon={<Download className="h-4 w-4" />}
                disabled={exportLoading || loadingTasks}
                className={`w-full sm:w-auto h-10${selectedRows.size > 0
                    ? " bg-blue-50 border-blue-200 text-blue-600"
                    : ""
                  }`}
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleTasksFollowupsSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="opportunity_management" action="create">
            <Button
              variant="outline"
              onClick={() => setShowBulkUploadModal(true)}
              className="w-full sm:w-auto h-10 border-blue-200 text-blue-600 hover:bg-blue-50"
              icon={<Upload className="h-4 w-4" />}
            >
              Bulk Upload
            </Button>
          </PermissionGate>
          {selectedRows.size > 0 && (
            <PermissionGate permission="opportunity_management" action="delete">
              <Button
                variant="outline"
                onClick={handleBulkDelete}
                className="w-full sm:w-auto h-10 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                icon={<Trash2 className="h-4 w-4" />}
                loading={isBulkDeleting}
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="opportunity_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreateTask}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen h-10"
            >
              Create Task
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {filterOwner !== "all" ? "Representative Tasks" : "Total Tasks"}
              </p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(stats.total)}</p>
              {filterOwner !== "all" && (
                <p className="text-xs text-gray-500 mt-1">{filterOwner}</p>
              )}
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {filterOwner !== "all" ? "Rep Completed" : "Completed"}
              </p>
              <p className="text-2xl font-bold text-green-600">
                {formatIndianNumber(stats.completed)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {filterOwner !== "all" ? "Rep Pending" : "Pending"}
              </p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatIndianNumber(stats.pending)}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {filterOwner !== "all" ? "Rep Overdue" : "Overdue"}
              </p>
              <p className="text-2xl font-bold text-red-600">{formatIndianNumber(stats.overdue)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-row flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search tasks by Task ID, Lead ID, Opportunity, Title, Owner..."
              value={searchTerm}
              onChange={(e) => {
                handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search);
                setCurrentPage(1); // Reset to first page when searching
              }}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full"
              autoComplete="off"
            />
          </div>
          {!isSalesExecutive && (
            <div className="relative">
              <button
                ref={assigneeDropdownRef}
                onClick={() => {
                  const isOpening = !openAssigneeDropdown;
                  setOpenAssigneeDropdown(isOpening);
                  if (isOpening) {
                    setFilteredAssigneeOptions(assigneeOptions);
                  }
                }}
                disabled={loadingAssignees}
                className="px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <span className="truncate text-sm whitespace-nowrap">
                  {assigneeOptions.find(opt => opt.id === filterOwner)?.name || 'All Representatives'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Assignee Filter Dropdown */}
              {openAssigneeDropdown && (
                <div
                  data-dropdown-task-assignee-filter
                  className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                  style={{
                    top: `${assigneeDropdownPosition.top}px`,
                    left: `${assigneeDropdownPosition.left}px`,
                    minWidth: '240px',
                    maxWidth: '350px',
                    width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                  }}
                >
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={assigneeSearchTerm}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                          setAssigneeSearchTerm(value);
                        }}
                        placeholder="Search assignees..."
                        maxLength={100}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                    {loadingAssignees && filteredAssigneeOptions.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 text-center">
                        Loading assignees...
                      </div>
                    ) : filteredAssigneeOptions.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 text-center">
                        {assigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        {filteredAssigneeOptions.map(option => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              setFilterOwner(option.id);
                              setCurrentPage(1);
                              setOpenAssigneeDropdown(false);
                              setAssigneeSearchTerm('');
                            }}
                            className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filterOwner === option.id
                              ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                              : 'hover:bg-gray-50 text-gray-700'
                              }`}
                          >
                            <div className="truncate font-medium break-words">{option.name}</div>
                            {(option.email || option.role) && (
                              <div className="text-xs text-gray-500 truncate break-words flex flex-wrap items-center gap-1.5 mt-0.5">
                                {option.email && <span>{option.email}</span>}
                                {option.email && option.role && <span className="text-gray-300">•</span>}
                                {option.role && <span className="capitalize px-1.5 py-0.25 bg-gray-100 rounded text-[10px] text-gray-600 font-medium">{option.role.replace('_', ' ')}</span>}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <select
            value={filterPriority}
            onChange={(e) => {
              setFilterPriority(e.target.value);
              setCurrentPage(1); // Reset to first page when filtering
            }}
            className="flex-shrink-0 min-w-[150px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Priorities</option>
            {priorities.map((priority) => (
              <option key={priority} value={priority.toLowerCase()}>
                {priority}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1); // Reset to first page when filtering
            }}
            className="flex-shrink-0 min-w-[150px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(newRange) => {
              setDateRange(newRange);
              setCurrentPage(1);
            }}
            allowFutureDates={true}
          />
          {hasActiveFilters && (
            <Button
              variant="outline"
              icon={<X className="h-4 w-4" />}
              onClick={handleClearFilters}
              className="flex-shrink-0 whitespace-nowrap"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loadingTasks ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
            <p className="text-gray-600">Loading tasks...</p>
          </div>
        ) : tasksError ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error loading tasks
            </h3>
            <p className="text-gray-500">
              Failed to load tasks. Please try again.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto w-full">
              <table className="min-w-full divide-y divide-gray-200 table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                        checked={sortedTasks.length > 0 && sortedTasks.every(task => selectedRows.has(task.id))}
                        onChange={() => handleSelectAll(sortedTasks)}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('taskId')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Task ID</span>
                        {sortColumn === 'taskId' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Title</span>
                        {sortColumn === 'title' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('priority')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Priority</span>
                        {sortColumn === 'priority' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === 'status' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('dueDateRaw')}
                    >
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>Due Date</span>
                        {sortColumn === 'dueDateRaw' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('owner')}
                    >
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>Assigned To</span>
                        {sortColumn === 'owner' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                      <div className="flex items-center space-x-1">
                        <Target className="h-3 w-3" />
                        <span>Opportunity</span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('createdDateRaw')}
                    >
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>Created Date</span>
                        {sortColumn === 'createdDateRaw' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTasks.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center">
                        <Target className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No tasks found
                        </h3>
                        <p className="text-gray-500 mb-4">
                          Get started by creating your first task
                        </p>
                        <PermissionGate permission="opportunity_management" action="create">
                          <Button
                            variant="primary"
                            icon={<Plus className="h-4 w-4" />}
                            onClick={handleCreateTask}
                            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                          >
                            Create Task
                          </Button>
                        </PermissionGate>
                      </td>
                    </tr>
                  ) : (
                    sortedTasks.map((task) => (
                      <tr
                        key={task.id}
                        className={`hover:bg-gray-50 transition-colors ${isOverdue(task.dueDateRaw, task.dueTime) &&
                          task.status !== "Completed" && task.status !== "Cancelled"
                          ? "bg-red-50"
                          : ""
                          }`}
                      >
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                            checked={selectedRows.has(task.id)}
                            onChange={() => handleSelectRow(task.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {task.taskId ? (
                            <button
                              type="button"
                              onClick={() => openViewModal(task)}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-50 text-agrosaf-green border border-green-200 hover:bg-green-100 hover:border-green-300 transition-colors cursor-pointer"
                              title="Click to view task details"
                            >
                              {task.taskId}
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs font-medium">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 capitalize truncate max-w-[250px]" title={task.title}>
                              {task.title}
                            </p>

                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                              task.priority
                            )}`}
                          >
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1 items-start">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                task.status
                              )}`}
                            >
                              {task.status}
                            </span>
                            {isOverdue(task.dueDateRaw, task.dueTime) &&
                              task.status !== "Completed" && task.status !== "Cancelled" && (
                                <span className="inline-flex items-center text-xs font-medium text-red-600">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Overdue
                                </span>
                              )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {task.dueDateRaw
                            ? formatDate(task.dueDateRaw, {
                              includeTime: false,
                            })
                            : task.dueDate || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 truncate max-w-[160px]" title={task.owner}>
                              {task.owner}
                            </span>
                            {task.ownerEmail && (
                              <span className="text-xs text-gray-400 truncate max-w-[160px]" title={task.ownerEmail}>
                                {task.ownerEmail}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {task.opportunity ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-700 truncate max-w-[150px] capitalize" title={task.opportunity.name}>
                                {task.opportunity.name}
                              </span>
                              {task.opportunity.leadId && (
                                <span className="text-xs text-gray-500 font-mono truncate max-w-[150px]" title={task.opportunity.leadId}>
                                  {task.opportunity.leadId}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {task.createdDateRaw
                            ? formatDate(task.createdDateRaw, {
                              includeTime: true,
                            })
                            : task.createdDate || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewModal(task);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {task.status !== "Completed" && (
                              <PermissionGate
                                permission="opportunity_management"
                                action="update"
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCompleteModal(task.id);
                                  }}
                                  className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                  title="Complete"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {task.status !== "Completed" && (
                              <PermissionGate
                                permission="opportunity_management"
                                action="update"
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTask(task.id);
                                  }}
                                  className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            <PermissionGate
                              permission="opportunity_management"
                              action="delete"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(task.id);
                                }}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4 bg-gray-50">
              {sortedTasks.map((task) => (
                <div
                  key={task.id}
                  className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm ${isOverdue(task.dueDateRaw, task.dueTime) &&
                    task.status !== "Completed" && task.status !== "Cancelled"
                    ? "border-red-300 bg-red-50"
                    : ""
                    }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                        checked={selectedRows.has(task.id)}
                        onChange={() => handleSelectRow(task.id)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap gap-y-1">
                        {task.taskId && (
                          <button
                            type="button"
                            onClick={() => openViewModal(task)}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-50 text-agrosaf-green border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                            title="Click to view task details"
                          >
                            {task.taskId}
                          </button>
                        )}
                        <h4 className="font-medium text-gray-900 capitalize truncate">
                          {task.title}
                        </h4>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                            task.priority
                          )}`}
                        >
                          {task.priority}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            task.status
                          )}`}
                        >
                          {task.status}
                        </span>
                        {isOverdue(task.dueDateRaw, task.dueTime) &&
                          task.status !== "Completed" && task.status !== "Cancelled" && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Overdue
                            </span>
                          )}
                      </div>

                      <div className="space-y-2 text-sm text-gray-500 mb-3">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {task.dueDateRaw
                              ? formatDate(task.dueDateRaw, { includeTime: false })
                              : task.dueDate || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>{task.owner}</span>
                        </div>
                        {task.opportunity && (
                          <div className="flex items-center space-x-2">
                            <Target className="h-4 w-4" />
                            <span className="text-gray-900 font-medium truncate">
                              {task.opportunity.name}
                            </span>
                            {task.opportunity.leadId && (
                              <span className="text-xs text-gray-500 font-mono truncate">
                                ({task.opportunity.leadId})
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>
                            Created: {task.createdDateRaw
                              ? formatDate(task.createdDateRaw, { includeTime: true })
                              : task.createdDate || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-3 mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openViewModal(task);
                      }}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center"
                    >
                      <Eye className="h-4 w-4 mr-1" /> View
                    </button>

                    {task.status !== "Completed" && (
                      <PermissionGate
                        permission="opportunity_management"
                        action="update"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCompleteModal(task.id);
                          }}
                          className="text-green-600 hover:text-green-900 text-sm font-medium flex items-center"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" /> Complete
                        </button>
                      </PermissionGate>
                    )}

                    {task.status !== "Completed" && (
                      <PermissionGate permission="opportunity_management" action="update">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTask(task.id);
                          }}
                          className="text-green-600 hover:text-green-900 text-sm font-medium flex items-center"
                        >
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </button>
                      </PermissionGate>
                    )}
                    <PermissionGate permission="opportunity_management" action="delete">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(task.id);
                        }}
                        className="text-red-600 hover:text-red-900 text-sm font-medium flex items-center"
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              ))}

              {sortedTasks.length === 0 && (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No tasks found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Get started by creating your first task
                  </p>
                  <PermissionGate permission="opportunity_management" action="create">
                    <Button
                      variant="primary"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={handleCreateTask}
                      className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                      Create Task
                    </Button>
                  </PermissionGate>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {shouldShowPagination && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            {/* Results Count and Limit Selector */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Showing{" "}
                {((pagination.page || currentPage) - 1) *
                  effectivePaginationLimit +
                  1}{" "}
                to{" "}
                {Math.min(
                  (pagination.page || currentPage) * effectivePaginationLimit,
                  totalTaskCount
                )}{" "}
                of {totalTaskCount} results
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Items per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() =>
                  handlePageChange((pagination.page || currentPage) - 1)
                }
                disabled={(pagination.page || currentPage) <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const totalPages = totalTaskPages || 1;
                  const currentPageNum = pagination.page || currentPage;
                  const pages = [];

                  if (totalPages <= 10) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);

                    if (currentPageNum <= 4) {
                      for (let i = 2; i <= 5; i++) {
                        pages.push(i);
                      }
                      pages.push("ellipsis");
                      pages.push(totalPages);
                    } else if (currentPageNum >= totalPages - 3) {
                      pages.push("ellipsis");
                      for (let i = totalPages - 4; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push("ellipsis");
                      for (
                        let i = currentPageNum - 1;
                        i <= currentPageNum + 1;
                        i++
                      ) {
                        pages.push(i);
                      }
                      pages.push("ellipsis");
                      pages.push(totalPages);
                    }
                  }

                  return pages.map((page, index) => {
                    if (page === "ellipsis") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-2 text-gray-500"
                        >
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                          ? "bg-agrosaf-green text-white"
                          : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
              </div>

              <button
                onClick={() =>
                  handlePageChange((pagination.page || currentPage) + 1)
                }
                disabled={(pagination.page || currentPage) >= totalTaskPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            {/* Fixed Header */}
            <div className="text-gray-900 px-6 py-4 rounded-t-xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold">
                    {isEditing ? "Edit Task" : "Create New Task"}
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    {isEditing
                      ? "Update task details and follow-up information"
                      : "Capture task details and follow-up information"}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm({
                      title: "",
                      type: "",
                      description: "",
                      dueDate: "",
                      dueTime: "",
                      owner: "",
                      priority: "Low",
                      status: "Pending",
                      notes: "",
                      opportunity: "",
                      techRequired: "",
                    });
                    setOpportunitySearchTerm("");
                    setModalAssigneeSearchTerm("");
                    setFormErrors({});
                  }}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-grow">
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => {
                      const value = handleTextInputChange(
                        e,
                        null,
                        FIELD_LIMITS.customerName
                      );
                      setCreateForm((prev) => ({ ...prev, title: value }));
                      if (formErrors.create_title) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.create_title;
                          return newErrors;
                        });
                      }
                    }}
                    maxLength={FIELD_LIMITS.customerName}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.create_title
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="Enter task title"
                    onBlur={() => handleBlur("title")}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-red-500">
                      {formErrors.create_title && formErrors.create_title}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(
                        createForm.title,
                        FIELD_LIMITS.customerName
                      )}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={createForm.type}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, type: e.target.value }));
                      if (formErrors.create_type) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.create_type;
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.create_type ? "border-red-500" : "border-gray-300"
                      }`}
                    onBlur={() => handleBlur("type")}
                  >
                    <option value="">Select Type</option>
                    {taskTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {formErrors.create_type && (
                    <p className="text-xs text-red-500 mt-1">
                      {formErrors.create_type}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tech Required <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.techRequired}
                    onChange={(e) => {
                      const value = handleTextInputChange(
                        e,
                        null,
                        FIELD_LIMITS.notes
                      );
                      setCreateForm((prev) => ({ ...prev, techRequired: value }));
                      if (formErrors.create_techRequired) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.create_techRequired;
                          return newErrors;
                        });
                      }
                    }}
                    maxLength={FIELD_LIMITS.notes}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.create_techRequired
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="Enter technical requirements"
                    onBlur={() => handleBlur("techRequired")}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-red-500">
                      {formErrors.create_techRequired && formErrors.create_techRequired}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(
                        createForm.techRequired,
                        FIELD_LIMITS.notes
                      )}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => {
                      let value = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                      value = sanitizeText(value);
                      setCreateForm((prev) => ({ ...prev, description: value }));
                      if (formErrors.create_description) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.create_description;
                          return newErrors;
                        });
                      }
                    }}
                    rows={3}
                    maxLength={FIELD_LIMITS.notes}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.create_description
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder="Enter task description"
                    onBlur={() => handleBlur("description")}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-red-500">
                      {formErrors.create_description && formErrors.create_description}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(
                        createForm.description,
                        FIELD_LIMITS.notes
                      )}
                    </span>
                  </div>
                </div>

                {/* Opportunity Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunity <span className="text-red-500">*</span>
                  </label>                  <SearchableDropdown
                    options={opportunityOptions}
                    value={createForm.opportunity || null}
                    onChange={(selectedId) => {
                      setCreateForm((prev) => ({
                        ...prev,
                        opportunity: selectedId || "",
                      }));
                      if (formErrors.create_opportunity) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.create_opportunity;
                          return newErrors;
                        });
                      }
                    }}
                    onSearch={(searchTerm) => {
                      setOpportunitySearchTerm(searchTerm);
                    }}
                    placeholder={
                      loadingOpportunities
                        ? "Loading opportunities..."
                        : "Select Opportunity"
                    }
                    loading={loadingOpportunities}
                    error={formErrors.create_opportunity}
                    searchPlaceholder="Search by opportunity name or account..."
                    minSearchLength={2}
                    debounceMs={300}
                    className={
                      formErrors.create_opportunity ? "border-red-500" : ""
                    }
                    onBlur={() => handleBlur("opportunity")}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={createForm.dueDate}
                      min={minDate}
                      onChange={(e) => {
                        const selectedDate = e.target.value;

                        // Validate that date is not before the minimum allowed date
                        if (selectedDate && selectedDate < minDate) {
                          const errorMessage = minDate < todayStr
                            ? `Date cannot be earlier than ${minDate}. You can only edit up to the original date.`
                            : "Due date cannot be in the past. Please select today or a future date.";
                          setFormErrors((prev) => ({
                            ...prev,
                            create_dueDate: errorMessage,
                          }));
                          toast.error(errorMessage);
                          return;
                        }

                        setCreateForm((prev) => ({
                          ...prev,
                          dueDate: selectedDate,
                        }));
                        if (formErrors.create_dueDate) {
                          setFormErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.create_dueDate;
                            return newErrors;
                          });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.create_dueDate
                        ? "border-red-500"
                        : "border-gray-300"
                        }`}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      onBlur={() => handleBlur("dueDate")}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-red-500">
                        {formErrors.create_dueDate && formErrors.create_dueDate}
                      </span>
                      <span className="text-xs text-gray-500">
                        {/* No character counter for date inputs */}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Due Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={createForm.dueTime}
                      onChange={(e) => {
                        setCreateForm((prev) => ({
                          ...prev,
                          dueTime: e.target.value,
                        }));
                        if (formErrors.create_dueTime) {
                          setFormErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.create_dueTime;
                            return newErrors;
                          });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.create_dueTime
                        ? "border-red-500"
                        : "border-gray-300"
                        }`}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      onBlur={() => handleBlur("dueTime")}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-red-500">
                        {formErrors.create_dueTime && formErrors.create_dueTime}
                      </span>
                      <span className="text-xs text-gray-500">
                        {/* No character counter for time inputs */}
                      </span>
                    </div>                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned to <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={modalAssigneeDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isSalesExecutive) return;
                          const isOpening = !openModalAssigneeDropdown;
                          setOpenModalAssigneeDropdown(isOpening);
                          if (isOpening) {
                            setFilteredModalAssigneeOptions(modalAssigneeOptions);
                          }
                        }}
                        disabled={loadingModalAssignees || isSalesExecutive}
                        className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${formErrors.create_owner ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <span className={`truncate text-sm ${createForm.owner ? 'text-gray-900' : 'text-gray-400'}`}>
                          {createForm.owner
                            ? (modalAssigneeOptions.find(opt => opt.id === createForm.owner)?.name || createForm.ownerDisplayName || 'Selected')
                            : (loadingModalAssignees ? 'Loading representatives...' : 'Select Representative')}
                        </span>
                        {!isSalesExecutive && <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openModalAssigneeDropdown ? 'rotate-180' : ''}`} />}
                      </button>

                      {/* Modal Assignee Dropdown */}
                      {openModalAssigneeDropdown && (
                        <div
                          data-dropdown-task-assignee-modal
                          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                          style={{
                            top: `${modalAssigneeDropdownPosition.top}px`,
                            left: `${modalAssigneeDropdownPosition.left}px`,
                            minWidth: '240px',
                            maxWidth: '350px',
                            width: `${Math.max(240, modalAssigneeDropdownPosition.width)}px`
                          }}
                        >
                          <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                value={modalAssigneeSearchTerm}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                  setModalAssigneeSearchTerm(value);
                                }}
                                placeholder="Search assignees..."
                                maxLength={100}
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                            {loadingModalAssignees && filteredModalAssigneeOptions.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 text-center">
                                Loading assignees...
                              </div>
                            ) : filteredModalAssigneeOptions.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 text-center">
                                {modalAssigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                              </div>
                            ) : (
                              <div className="flex flex-col">
                                {filteredModalAssigneeOptions.map(option => (
                                  <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => {
                                      setCreateForm(prev => ({
                                        ...prev,
                                        owner: option.id,
                                        ownerDisplayName: option.name,
                                        ownerEmail: option.email || '',
                                      }));
                                      if (formErrors.create_owner) {
                                        setFormErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors.create_owner;
                                          return newErrors;
                                        });
                                      }
                                      setOpenModalAssigneeDropdown(false);
                                      setModalAssigneeSearchTerm('');
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${createForm.owner === option.id
                                      ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                                      : 'hover:bg-gray-50 text-gray-700'
                                      }`}
                                  >
                                    <div className="truncate font-medium break-words">{option.name}</div>
                                    {(option.email || option.role) && (
                                      <div className="text-xs text-gray-500 truncate break-words flex flex-wrap items-center gap-1.5 mt-0.5">
                                        {option.email && <span>{option.email}</span>}
                                        {option.email && option.role && <span className="text-gray-300">•</span>}
                                        {option.role && <span className="capitalize px-1.5 py-0.25 bg-gray-100 rounded text-[10px] text-gray-600 font-medium">{option.role.replace('_', ' ')}</span>}
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {formErrors.create_owner && (
                      <span className="text-xs text-red-500 mt-1">{formErrors.create_owner.replace("Owner is required", "Representative is required")}</span>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={createForm.priority}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          priority: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      {priorities.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={createForm.status}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => {
                      let value = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                      value = sanitizeText(value);
                      setCreateForm((prev) => ({ ...prev, notes: value }));
                    }}
                    rows={3}
                    maxLength={FIELD_LIMITS.notes}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    placeholder="Enter any additional notes"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-red-500">
                      {/* Notes field doesn't have required validation, but keeping the structure for consistency */}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(createForm.notes, FIELD_LIMITS.notes)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-xl flex-shrink-0">
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setIsEditing(false);
                    setSelectedTask(null);
                    setCreateForm({
                      title: "",
                      type: "",
                      description: "",
                      dueDate: "",
                      dueTime: "",
                      owner: "",
                      priority: "Low",
                      status: "Pending",
                      notes: "",
                      opportunity: "",
                      techRequired: "",
                    });
                    setOpportunitySearchTerm("");
                    setModalAssigneeSearchTerm("");
                    setFormErrors({});
                  }}
                  className="px-4"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen px-6"
                  onClick={() => {
                    const requiredFields = ["title", "type", "description", "opportunity", "dueDate", "dueTime", "owner"];
                    const validation = validateRequiredFields(
                      createForm,
                      requiredFields
                    );

                    if (!validation.isValid) {
                      const createErrors = {};
                      Object.keys(validation.errors).forEach((key) => {
                        createErrors[`create_${key}`] = validation.errors[key];
                      });
                      setFormErrors(createErrors);
                      toast.error("Please fill in all required fields.");
                      return;
                    }
                    // Validate that date is not before the minimum allowed date
                    if (createForm.dueDate) {
                      if (createForm.dueDate < minDate) {
                        const errorMessage = minDate < todayStr
                          ? `Date cannot be earlier than ${minDate}. You can only edit up to the original date.`
                          : "Due date cannot be in the past. Please select today or a future date.";
                        setFormErrors((prev) => ({
                          ...prev,
                          create_dueDate: errorMessage,
                        }));
                        toast.error(errorMessage);
                        return;
                      }
                    }

                    // Validate that due time is provided when due date is provided
                    if (createForm.dueDate && !createForm.dueTime) {
                      setFormErrors((prev) => ({
                        ...prev,
                        create_dueTime: "Due time is required when due date is selected.",
                      }));
                      toast.error("Due time is required when due date is selected.");
                      return;
                    }
                    setFormErrors({});

                    if (!createForm.techRequired.trim()) {
                      setFormErrors((prev) => ({
                        ...prev,
                        create_techRequired: "Tech Required field is required",
                      }));
                      toast.error("Tech Required field is required");
                      return;
                    }

                    // Map form data to backend format
                    const assignedUser = modalAssigneeOptions.find(opt => opt.id === createForm.owner);
                    const taskData = {
                      title: createForm.title,
                      type: createForm.type || "Follow-up",
                      description: createForm.description,
                      dueDate: createForm.dueDate
                        ? new Date(createForm.dueDate).toISOString()
                        : null,
                      dueTime: createForm.dueTime,
                      owner: assignedUser?.name || createForm.ownerDisplayName || createForm.owner,
                      ownerId: createForm.owner,
                      ownerEmail: assignedUser?.email || createForm.ownerEmail || "",
                      priority: createForm.priority,
                      status: createForm.status || "Pending",
                      notes: createForm.notes || "",
                      opportunityId: createForm.opportunity || null,
                      techRequired: createForm.techRequired,
                    };

                    if (isEditing && selectedTask) {
                      // Check for changes in specific fields that require email notification
                      const changes = {};
                      let shouldSendEmail = false;

                      if (originalTaskValues) {
                        if (createForm.type !== originalTaskValues.type) {
                          changes.type = { old: originalTaskValues.type, new: createForm.type };
                          shouldSendEmail = true;
                        }

                        const newDueDate = createForm.dueDate ? new Date(createForm.dueDate).toISOString() : null;
                        const originalDueDate = originalTaskValues.dueDate ? new Date(originalTaskValues.dueDate).toISOString() : null;
                        if (newDueDate !== originalDueDate) {
                          changes.dueDate = { old: originalDueDate, new: newDueDate };
                          shouldSendEmail = true;
                        }

                        if (createForm.dueTime !== originalTaskValues.dueTime) {
                          changes.dueTime = { old: originalTaskValues.dueTime, new: createForm.dueTime };
                          shouldSendEmail = true;
                        }
                      }

                      // Include email notification data if changes detected
                      if (shouldSendEmail) {
                        taskData.sendUpdateEmail = true;
                        taskData.emailChanges = changes;
                      }

                      updateTaskMutation.mutate({
                        taskId: selectedTask.id,
                        taskData,
                      });
                    } else {
                      createTaskMutation.mutate(taskData);
                    }
                  }}
                  disabled={
                    createTaskMutation.isPending || updateTaskMutation.isPending
                  }
                >
                  {createTaskMutation.isPending || updateTaskMutation.isPending
                    ? isEditing
                      ? "Updating..."
                      : "Creating..."
                    : isEditing
                      ? "Update Task"
                      : "Create Task"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Confirmation Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Mark Task as Completed?
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to mark this task as completed?
            </p>
            <div className="flex items-center justify-end space-x-3">
              <Button variant="outline" onClick={cancelCompleteTask}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={confirmCompleteTask}
              >
                Yes, Complete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Task?
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this task? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <Button variant="outline" onClick={cancelDeleteTask}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmDeleteTask}
              >
                Yes, Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Task Details Modal */}
      {showViewModal && taskToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="text-gray-900 p-6 rounded-t-xl border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <Eye className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Task Details</h3>
                    <p className="text-gray-600 text-sm mt-1">View complete task information</p>
                  </div>
                </div>
                <button
                  onClick={closeViewModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable Area */}
            <div className="overflow-y-auto flex-grow">
              <div className="p-6 space-y-6">
                {/* Row 1: Task ID & Title */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {taskToView.taskId && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Task ID</label>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold bg-green-50 text-agrosaf-green border border-green-200">
                        {taskToView.taskId}
                      </span>
                    </div>
                  )}
                  <div className={`bg-gray-50 rounded-lg p-4 ${taskToView.taskId ? 'md:col-span-3' : 'w-full'}`}>
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Task Title</label>
                    <p className="text-xl font-bold text-gray-900 break-words capitalize" style={{ overflowWrap: 'anywhere' }}>{taskToView.title}</p>
                  </div>
                </div>

                {/* Row 2: Status, Priority, Type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor(taskToView.status)}`}>
                      {taskToView.status}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Priority</label>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getPriorityColor(taskToView.priority)}`}>
                      {taskToView.priority}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Type</label>
                    <span className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
                      {taskToView.type}
                    </span>
                  </div>
                </div>

                {/* Row 3: Due Date, Due Time, Assigned To */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Due Date</label>
                    <p className="text-lg font-bold text-gray-900">
                      {taskToView.dueDateRaw
                        ? formatDate(taskToView.dueDateRaw, { includeTime: false })
                        : taskToView.dueDate || "N/A"}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Due Time</label>
                    <p className="text-lg font-bold text-gray-900">{formatTimeTo12Hour(taskToView.dueTime)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Assigned To</label>
                    <p className="text-lg font-bold text-gray-900 capitalize">{taskToView.owner || "Unassigned"}</p>
                  </div>
                </div>

                {/* Row 4: Opportunity */}
                {taskToView.opportunity && (
                  <div className="bg-gray-50 rounded-lg p-4 w-full">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Opportunity</label>
                    <p className="text-lg font-bold text-gray-900 leading-snug break-words capitalize" style={{ overflowWrap: 'anywhere' }}>{taskToView.opportunity.name}</p>
                    {taskToView.opportunity.leadId && (
                      <p className="text-sm text-gray-500 font-mono mt-1 break-words" style={{ overflowWrap: 'anywhere' }}>Lead ID: {taskToView.opportunity.leadId}</p>
                    )}
                  </div>
                )}

                {/* Row 4.5: Tech Required */}
                {taskToView.techRequired && (
                  <div className="bg-gray-50 rounded-lg p-4 w-full">
                    <label className="text-sm font-semibold text-gray-500 mb-1 block uppercase tracking-wider ">Tech Required</label>
                    <p className="text-lg font-bold text-gray-900 leading-snug break-words capitalize" style={{ overflowWrap: 'anywhere' }}>{taskToView.techRequired}</p>
                  </div>
                )}

                {/* Row 5: Description */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-agrosaf-green" />
                    Description
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words first-letter:capitalize" style={{ overflowWrap: 'anywhere' }}>
                      {taskToView.description || <span className="text-gray-400 italic font-normal">No description provided</span>}
                    </p>
                  </div>
                </div>

                {/* Row 6: Notes */}
                {taskToView.notes && (
                  <div className="pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-agrosaf-green" />
                      Notes
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words first-letter:capitalize" style={{ overflowWrap: 'anywhere' }}>
                        {taskToView.notes}
                      </p>
                    </div>
                  </div>
                )}

                {/* Audit Trail */}
                <div className="pt-6 border-t border-gray-100 flex flex-wrap gap-8">
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Created By</label>
                    <p className="text-sm font-medium text-gray-600">
                      {taskToView.createdByName || "System"} • {taskToView.createdDateRaw ? formatDate(taskToView.createdDateRaw, { includeTime: false }) : (taskToView.createdDate || "N/A")}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Last Updated</label>
                    <p className="text-sm font-medium text-gray-600">
                      {taskToView.updatedByName || "System"} • {taskToView.lastModifiedRaw ? formatDate(taskToView.lastModifiedRaw, { includeTime: false }) : (taskToView.lastModified || "N/A")}
                    </p>
                  </div>
                  {taskToView.completedDateRaw && (
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-green-500 uppercase tracking-widest">Completed Date</label>
                      <p className="text-sm font-medium text-green-600">
                        {formatDate(taskToView.completedDateRaw, { includeTime: false })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-6 rounded-b-xl">
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={closeViewModal}
                  className="px-8"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Tasks"
        showDateRange
        scopedFormatExport={{
          onExport: handleTasksFollowupsScopedExport,
          formats: ["csv", "xlsx", "pdf"],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <div className="flex items-center space-x-3 text-red-600 mb-4">
              <AlertCircle className="h-6 w-6" />
              <h3 className="text-lg font-semibold">Delete Multiple Tasks?</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{selectedRows.size}</strong> selected tasks? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteConfirm(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmBulkDelete}
                loading={isBulkDeleting}
              >
                Yes, Delete All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["tasks"] });
            queryClient.invalidateQueries({ queryKey: ["taskStats"] });
          }}
          config={{
            title: "Bulk Upload Tasks",
            subtitle: "Download the template and upload multiple tasks at once",
            templateEndpoint: "/crm/tasks/bulk-upload/template",
            validateEndpoint: "/crm/tasks/bulk-upload/validate",
            uploadEndpoint: "/crm/tasks/bulk-upload",
            templateFilename: "tasks_bulk_template.xlsx",
            dataKey: "tasks",
            maxRecords: 100,
            allowedFormats: ["excel"],
            accept: ".xlsx,.xls",
            instructions: [
              "Maximum 100 tasks are allowed per file upload.",
              "Lead ID, Title, Due Date, and Due Time are required.",
              "Due Date must be in DD/MM/YYYY format.",
              "Due Time must be in HH:mm format (24-hour).",
              "Assigned To (Email, Name or Employee ID) is required.",
              "Priority can be 'High', 'Medium', or 'Low' (defaults to Medium).",
              "Status can be 'Pending', 'In Progress', 'Completed', or 'Cancelled'.",
              "Task Type can be 'Call', 'Meeting', 'Follow-up', 'Demo', 'Email', or 'Custom'.",
              "Lead ID should be the generated Lead ID from the Lead collection (e.g. LEAD-1001)."
            ]
          }}
        />
      )}
    </div>
  );
};

export default TasksFollowups;
